import { useCallback, useEffect, useReducer, useRef } from 'react';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { auth, storage } from '../firebase';
import UploadQueue from '../upload/uploadQueue';
import { diffAgainstExisting, videoKeyOf } from '../upload/dedup';
import { resolveFootageConflict, FOOTAGE_VIDEOS } from '../upload/footageKind';
import { scanVideos } from '../videoUpload/videoScan';
import { videoStoragePath } from '../videoUpload/videoKeys';
import { reorder, withOrder } from '../videoUpload/ordering';
import { evaluateVideoCaps } from '../videoUpload/videoCaps';
import { evaluateCaps } from '../photoUpload/caps';
import { fetchExistingVideoIds } from './useProjectVideos';
import { trackEvent } from '../analytics';

const BYTES_PER_MB = 1024 * 1024;
const toMb = (bytes) => Math.round((bytes || 0) / BYTES_PER_MB);

// Multi-video footage upload lifecycle (Phase 1 of the combine-at-ingest plan).
//
// Deliberately the SAME shape as usePhotoUpload — scan → preflight → queue →
// terminal phase — so the two flows stay comparable and share the queue, the
// dedup diff and the pooled-storage verdict. What is different is genuinely
// different:
//   * ORDER matters. Videos are concatenated in a later phase, so the preflight
//     is an ordered list the user can drag, and `order` is uploaded with each
//     file.
//   * No client thumbnails. A browser can't decode Brinno MJPEG, so posters
//     come from the server probe.
//   * A `conflict` phase for the photos-XOR-videos rule.
//
// State machine:
//   idle → scanning → preflight → uploading → done
//                  ↘ conflict     ↓      ↘
//                               error   partial ⇄ uploading (retryFailed)

const INITIAL_STATE = {
  phase: 'idle',
  scan: null,          // scanVideos() result
  dedup: null,         // { toUpload, alreadyUploaded, totals }
  ordered: [],         // the user-visible playback order (entries to upload)
  caps: null,          // evaluateVideoCaps() result
  poolCaps: null,      // evaluateCaps() pooled-storage verdict
  progress: null,
  itemStates: [],
  conflict: null,      // resolveFootageConflict() result
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'scanStart':
      return { ...INITIAL_STATE, phase: 'scanning' };
    case 'conflict':
      return { ...INITIAL_STATE, phase: 'conflict', conflict: action.conflict };
    case 'scanDone':
      return {
        ...state,
        phase: 'preflight',
        scan: action.scan,
        dedup: action.dedup,
        ordered: action.ordered,
        caps: action.caps,
        poolCaps: action.poolCaps,
      };
    case 'reorder':
      return { ...state, ordered: action.ordered };
    case 'capsUpdate':
      return { ...state, caps: action.caps, poolCaps: action.poolCaps };
    case 'startUploading':
      return { ...state, phase: 'uploading' };
    case 'progress':
      return { ...state, progress: action.aggregate, itemStates: action.items };
    case 'finish':
      return { ...state, phase: action.phase, progress: action.aggregate, itemStates: action.items };
    case 'error':
      return { ...state, phase: 'error', error: action.error };
    case 'reset':
      return INITIAL_STATE;
    default:
      return state;
  }
}

// Wires the queue to Firebase Storage. Injectable so tests drive the hook
// without touching Firebase.
//
// `orderBase` is one past the highest `order` already stored in the project
// (see ordering.js `nextOrderBase`): `order` is a project-wide playback index,
// not a batch-local one, so appending a second batch must continue the sequence
// rather than restart it — or collide with it.
export function defaultBuildVideoUploader({ uid, projectId, orderBase = 0 }) {
  return async function uploadItem(item, { onProgress, signal }) {
    const path = videoStoragePath(uid, projectId, item.videoKey);
    const metadata = {
      // Explicit, because a file picked off an SD card often has an empty
      // `File.type` and storage.rules requires `video/*` on this path.
      contentType: item.file?.type || 'video/mp4',
      customMetadata: {
        fileName: item.fileName || '',
        capturedAt: item.capturedAt != null ? String(item.capturedAt) : '',
        capturedAtSource: item.capturedAtSource || 'fileModified',
        order: String(orderBase + (item.order ?? 0)),
        clientKey: item.videoKey,
      },
    };

    await new Promise((resolve, reject) => {
      const task = uploadBytesResumable(ref(storage, path), item.file, metadata);
      const abortHandler = () => { try { task.cancel(); } catch (_e) { /* ignore */ } };
      if (signal) {
        if (signal.aborted) abortHandler();
        else signal.addEventListener('abort', abortHandler, { once: true });
      }
      task.on(
        'state_changed',
        (snap) => onProgress({ bytesTransferred: snap.bytesTransferred, totalBytes: snap.totalBytes }),
        (err) => {
          if (signal) signal.removeEventListener('abort', abortHandler);
          reject(err);
        },
        () => {
          if (signal) signal.removeEventListener('abort', abortHandler);
          resolve();
        },
      );
    });

    return { storagePath: path };
  };
}

export default function useMultiVideoUpload(projectId, {
  buildUploader = defaultBuildVideoUploader,
  fetchExistingIds = fetchExistingVideoIds,
  runScan = scanVideos,
  // Footage already in the project — drives the file-count cap.
  existingVideos = 0,
  existingBytes = 0,
  // One past the highest `order` already stored, from ordering.js
  // `nextOrderBase`. Deliberately NOT derived from `existingVideos` here: a
  // count is wrong the moment a partial failure leaves a gap in the stored
  // orders, which is precisely the resume case this base exists for.
  orderBase = 0,
  // Photos-XOR-videos inputs (see upload/footageKind.js).
  hasPhotos = false,
  hasLegacySourceVideo = false,
  // Pooled ACCOUNT storage, exactly as the photo flow meters it — footage
  // bytes land in the same pool, so there is one ceiling, not two.
  poolTotalBytes = null,
  poolUsedBytes = 0,
  poolLoading = false,
  // Concurrency 2, not the photo default of 5: these are multi-GB files on a
  // consumer uplink, and five in flight just splits the same bandwidth into
  // five slower streams while multiplying the memory the SDK buffers.
  concurrency = 2,
  createQueue = (opts) => new UploadQueue(opts),
} = {}) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const queueRef = useRef(null);
  const batchItemsRef = useRef([]);
  // The order base is FROZEN when a batch starts, not read live. `orderBase`
  // comes from a live Firestore subscription, so every clip that lands
  // mid-batch bumps it. Reading it again on retry would re-base the retried
  // clips above the ones that already succeeded — a batch of 5 that loses items
  // 2 and 4 would come back as 0,1,3,5,7 and play scrambled.
  const frozenOrderBaseRef = useRef(0);

  const preflight = useCallback(async (files) => {
    const conflict = resolveFootageConflict({
      incomingKind: FOOTAGE_VIDEOS,
      hasPhotos,
      hasLegacySourceVideo,
    });
    if (conflict) {
      dispatch({ type: 'conflict', conflict });
      trackEvent('video_upload_blocked', { project_id: projectId, reason: 'footage_kind_conflict' });
      return;
    }
    dispatch({ type: 'scanStart' });
    try {
      const scan = await runScan(files);
      const existingIds = await fetchExistingIds(projectId);
      const dedup = diffAgainstExisting(scan.entries, existingIds, { keyOf: videoKeyOf });
      const ordered = withOrder(dedup.toUpload);
      const caps = evaluateVideoCaps({
        existingVideos,
        batchVideos: dedup.totals.toUploadCount,
        batchBytes: dedup.totals.toUploadBytes,
        entries: dedup.toUpload,
      });
      const poolCaps = evaluateCaps({
        existingBytes,
        batchBytes: dedup.totals.toUploadBytes,
        poolTotalBytes,
        poolUsedBytes,
        poolLoading,
      });
      dispatch({ type: 'scanDone', scan, dedup, ordered, caps, poolCaps });
      trackEvent('video_preflight_scanned', {
        project_id: projectId,
        video_count: dedup.totals.toUploadCount,
        scanned_count: scan.totals.videoCount,
        already_uploaded_count: dedup.totals.alreadyUploadedCount,
        skipped_non_videos: scan.skippedNonVideos.length,
        ordering_basis: scan.orderingBasis,
        total_mb: toMb(dedup.totals.toUploadBytes),
        blocked: !!(caps.blocked || poolCaps.blocked),
      });
    } catch (err) {
      dispatch({ type: 'error', error: err });
      trackEvent('video_upload_error', {
        project_id: projectId,
        phase: 'preflight',
        error: (err && err.message) || String(err),
      });
    }
  }, [
    projectId, runScan, fetchExistingIds, existingVideos, existingBytes,
    hasPhotos, hasLegacySourceVideo, poolTotalBytes, poolUsedBytes, poolLoading,
  ]);

  // Re-verdict the pooled dimension while a preflight is open, for the same
  // reason usePhotoUpload does: the account rollup and the tier entitlement
  // resolve asynchronously, and a dialog opened inside that window would stay
  // stuck on "calculating…" with Start disabled (#362).
  useEffect(() => {
    if (state.phase !== 'preflight' || !state.dedup) return;
    dispatch({
      type: 'capsUpdate',
      caps: evaluateVideoCaps({
        existingVideos,
        batchVideos: state.dedup.totals.toUploadCount,
        batchBytes: state.dedup.totals.toUploadBytes,
        entries: state.dedup.toUpload,
      }),
      poolCaps: evaluateCaps({
        existingBytes,
        batchBytes: state.dedup.totals.toUploadBytes,
        poolTotalBytes,
        poolUsedBytes,
        poolLoading,
      }),
    });
  }, [
    poolTotalBytes, poolUsedBytes, poolLoading, existingVideos, existingBytes,
    state.phase, state.dedup,
  ]);

  const moveItem = useCallback((fromIndex, toIndex) => {
    dispatch({ type: 'reorder', ordered: withOrder(reorder(state.ordered, fromIndex, toIndex)) });
  }, [state.ordered]);

  const runQueue = useCallback((items, { isRetry = false } = {}) => {
    const user = auth.currentUser;
    if (!user) {
      dispatch({ type: 'error', error: new Error('Not signed in') });
      trackEvent('video_upload_error', { project_id: projectId, phase: 'start', error: 'Not signed in' });
      return false;
    }
    const uploadItem = buildUploader({
      uid: user.uid, projectId, orderBase: frozenOrderBaseRef.current,
    });
    const queue = createQueue({ items, uploadItem, concurrency });
    queueRef.current = queue;
    dispatch({ type: 'startUploading' });
    const unsubscribe = queue.subscribe((evt) => {
      if (evt.type === 'progress' || evt.type === 'itemDone' || evt.type === 'itemFailed') {
        dispatch({ type: 'progress', aggregate: evt.aggregate || queue.getAggregate(), items: queue.getItems() });
      }
      if (evt.type === 'done') {
        const agg = evt.aggregate || queue.getAggregate();
        const cancelled = queue.state === 'cancelled';
        const failedCount = agg?.failedCount || 0;
        // failedCount > 0 means clips silently dropped — do NOT report success.
        const finalPhase = cancelled ? 'cancelled' : (failedCount > 0 ? 'partial' : 'done');
        if (cancelled) {
          trackEvent('video_upload_cancelled', {
            project_id: projectId,
            uploaded_count: agg?.doneCount || 0,
            remaining_count: Math.max(0, (agg?.totalItems || 0) - (agg?.doneCount || 0)),
          });
        } else {
          trackEvent('video_upload_completed', {
            project_id: projectId,
            video_count: agg?.totalItems || 0,
            uploaded_count: agg?.doneCount || 0,
            failed_count: failedCount,
            outcome: failedCount > 0 ? 'partial' : 'complete',
            is_retry: isRetry,
            total_mb: toMb(agg?.totalBytes),
          });
        }
        dispatch({ type: 'finish', phase: finalPhase, aggregate: agg, items: queue.getItems() });
        unsubscribe();
      }
    });
    queue.start();
    return true;
  }, [buildUploader, createQueue, concurrency, projectId]);

  const startUpload = useCallback(() => {
    const items = state.ordered;
    if (!items || items.length === 0) return;
    if (state.caps?.blocked || state.poolCaps?.blocked) return;
    batchItemsRef.current = items;
    // Freeze the base for this batch — every retry of it must reuse this
    // number, not the base that has grown as clips landed.
    frozenOrderBaseRef.current = orderBase;
    if (!runQueue(items)) return;
    trackEvent('video_upload_started', {
      project_id: projectId,
      video_count: items.length,
      total_mb: toMb(state.dedup?.totals?.toUploadBytes),
    });
  }, [state.ordered, state.caps, state.poolCaps, state.dedup, runQueue, projectId, orderBase]);

  const retryFailed = useCallback(() => {
    const failedKeys = new Set(
      (queueRef.current?.getItems() || [])
        .filter((it) => it.status === 'failed')
        .map((it) => it.videoKey),
    );
    if (failedKeys.size === 0) return;
    const retryItems = batchItemsRef.current.filter((e) => failedKeys.has(e.videoKey));
    if (retryItems.length === 0) return;
    batchItemsRef.current = retryItems;
    trackEvent('video_upload_retry_failed', { project_id: projectId, retry_count: retryItems.length });
    runQueue(retryItems, { isRetry: true });
  }, [runQueue, projectId]);

  const cancelUpload = useCallback(() => {
    if (queueRef.current) queueRef.current.cancel();
  }, []);

  const reset = useCallback(() => {
    if (queueRef.current) queueRef.current.cancel();
    queueRef.current = null;
    dispatch({ type: 'reset' });
  }, []);

  // Cancel in-flight uploads on unmount so a route change can't leak the
  // queue's timers / abort handlers.
  useEffect(() => () => {
    if (queueRef.current) queueRef.current.cancel();
  }, []);

  return {
    phase: state.phase,
    scan: state.scan,
    dedup: state.dedup,
    ordered: state.ordered,
    caps: state.caps,
    poolCaps: state.poolCaps,
    progress: state.progress,
    itemStates: state.itemStates,
    conflict: state.conflict,
    error: state.error,
    preflight,
    moveItem,
    startUpload,
    retryFailed,
    cancelUpload,
    reset,
  };
}
